import { functions } from "../firebase";

// Client wrappers for the unibox callables (functions/src/socialInbox.ts). The
// SocialInbox store is server-only (locked in firestore.rules), so the dashboard
// reads and replies exclusively through these Admin-SDK callables — never direct
// Firestore. Tokens never cross this boundary.

export type UniboxThread = {
  id: string;
  platform: "instagram" | "facebook" | "linkedin" | "whatsapp";
  kind: "dm" | "comment";
  participant: { id?: string; name?: string; handle?: string; avatarUrl?: string };
  lastSnippet: string;
  lastMessageAt: number;
  lastInboundAt: number;
  awaitingReply: boolean;
  unreadCount: number;
  state: "open" | "done" | "snoozed";
  snoozeUntil: number | null;
  leadRef: string | null;
  // Provider messaging-window deadline (ms epoch); WhatsApp stores its
  // customer-service deadline directly, while IG/FB derive it from inbound DM.
  // Null when the thread has no window (comments, LinkedIn).
  windowExpiresAt: number | null;
};

export type UniboxMessage = {
  id: string;
  direction: "in" | "out";
  text: string;
  at: number;
  attachmentType?: string | null;
};

export type UniboxThreadDetail = {
  id: string;
  platform: UniboxThread["platform"];
  kind: UniboxThread["kind"];
  participant: UniboxThread["participant"];
  state: UniboxThread["state"];
  snoozeUntil: number | null;
  leadRef: string | null;
  // Permalink of the post a comment thread sits on (null for DMs / unresolved).
  postPermalink?: string | null;
  lastInboundAt: number;
  awaitingReply: boolean;
  windowExpiresAt: number | null;
  messages: UniboxMessage[];
};

export const getUniboxThreads = async (params: {
  communityId: string;
  platform?: UniboxThread["platform"];
  kind?: UniboxThread["kind"];
  unreadOnly?: boolean;
  state?: UniboxThread["state"];
  sort?: "newest" | "oldestWaiting";
  limit?: number;
  // Pagination cursor: pass the previous response's nextCursor to load the
  // next page. Omit for the first page.
  cursor?: string | null;
  // Free-text search over participant name/handle + last message snippet.
  search?: string;
}): Promise<{ threads: UniboxThread[]; unreadTotal: number; nextCursor: string | null }> => {
  const callable = functions.httpsCallable("getUniboxThreads");
  const res = await callable(params);
  return res.data as { threads: UniboxThread[]; unreadTotal: number; nextCursor: string | null };
};

export const getUniboxThread = async (params: {
  communityId: string;
  threadId: string;
}): Promise<UniboxThreadDetail> => {
  const callable = functions.httpsCallable("getUniboxThread");
  const res = await callable(params);
  return res.data as UniboxThreadDetail;
};

export const sendUniboxMessage = async (params: {
  communityId: string;
  threadId: string;
  text: string;
}): Promise<{ ok: boolean }> => {
  const callable = functions.httpsCallable("sendUniboxMessage");
  const res = await callable(params);
  return res.data as { ok: boolean };
};

export const setUniboxThreadState = async (params: {
  communityId: string;
  threadId: string;
  state?: UniboxThread["state"];
  snoozeUntil?: number;
  markRead?: boolean;
}): Promise<{ ok: boolean }> => {
  const callable = functions.httpsCallable("setUniboxThreadState");
  const res = await callable(params);
  return res.data as { ok: boolean };
};

// Comment-to-DM automation: new inbound IG/FB comments matching a keyword get
// an automatic private reply (and optional public reply), evaluated by the Meta
// webhook on ingest. Same rules the MCP unibox_*_automation tools manage.
export type CommentToDmRule = {
  keyword: string;
  // Optional: a rule may be public-reply-only (X mentions have no auto-DM).
  dmText?: string;
  publicReply?: string;
  enabled?: boolean;
};

export const getUniboxAutomation = async (params: {
  communityId: string;
}): Promise<{ commentToDmRules: CommentToDmRule[]; cannedReplies: string[] }> => {
  const callable = functions.httpsCallable("getUniboxAutomation");
  const res = await callable(params);
  return res.data as { commentToDmRules: CommentToDmRule[]; cannedReplies: string[] };
};

export const setUniboxAutomation = async (params: {
  communityId: string;
  commentToDmRules: CommentToDmRule[];
  cannedReplies?: string[];
}): Promise<{ ok: boolean; commentToDmRules: CommentToDmRule[]; cannedReplies: string[] }> => {
  const callable = functions.httpsCallable("setUniboxAutomation");
  const res = await callable(params);
  return res.data as {
    ok: boolean;
    commentToDmRules: CommentToDmRule[];
    cannedReplies: string[];
  };
};

// AI draft reply: returns ONE suggested reply in the coach's voice for the
// operator to edit and send (draft-first, never auto-sends).
export const draftUniboxReply = async (params: {
  communityId: string;
  threadId: string;
}): Promise<{ draft: string }> => {
  const callable = functions.httpsCallable("draftUniboxReply");
  const res = await callable(params);
  return res.data as { draft: string };
};
